// =============================================================================
// FILE:
// frontend/src/pms/task_execution/tabs/ExecutionTab.js
// =============================================================================

import React, {
  useState,
  useEffect
} from "react";

import {
  Play,
  PauseCircle,
  CheckCircle2
} from "lucide-react";

import {

  createExecutionUpdate,

  getLatestExecutionStatus,

  getExecutionHistory,
  getMaterialConsumptionSummary,
  saveMaterialConsumption

} from "../services/taskExecutionService";

import {
  createMaterialReturn
} from "../../task_material/services/taskMaterialReturnService";

import {
  createMaterialScrap
} from "../../task_material/services/taskMaterialScrapService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function ExecutionTab({

  task

}) {


  // ===========================================================================
  // STATE
  // ===========================================================================
  const [executionStatus, setExecutionStatus] =
    useState(

      task.execution_status ||

      "NOT_STARTED"
    );

  const [progressPercent, setProgressPercent] =
    useState(0);

  const [workSummary, setWorkSummary] =
    useState("");

  const [blockers, setBlockers] =
    useState("");

  const [remarks, setRemarks] =
    useState("");

  const [saving, setSaving] =
    useState(false);

  const [executionHistory, setExecutionHistory] =
    useState([]);

   const [materialConsumption, setMaterialConsumption] =
  useState([]); 

  const [consumptionInputs, setConsumptionInputs] =
  useState({});

  const [returnInputs, setReturnInputs] =
  useState({});

  const [scrapInputs, setScrapInputs] =
  useState({});


  // ===========================================================================
  // LOAD LATEST STATUS
  // ===========================================================================
  const loadLatestExecutionStatus =
    async () => {

      try {

        const response =
          await getLatestExecutionStatus(
            task.id
          );

        const data =
          response?.data || {};

        setExecutionStatus(
          data.execution_status ||
          "NOT_STARTED"
        );

        setProgressPercent(
          data.progress_percent || 0
        );

      } catch (error) {

        console.error(
          "Failed to load execution status",
          error
        );
      }
    };


  // ===========================================================================
  // LOAD EXECUTION HISTORY
  // ===========================================================================
  const loadExecutionHistory =
    async () => {

      try {

        const response =
          await getExecutionHistory(
            task.id
          );

        setExecutionHistory(
          response?.data || []
        );

      } catch (error) {

        console.error(
          "Failed to load execution history",
          error
        );
      }
    };
  // ===========================================================================
// LOAD MATERIAL CONSUMPTION SUMMARY
// ===========================================================================
const loadMaterialConsumptionSummary =
  async () => {

    try {

      const response =
        await getMaterialConsumptionSummary(
          task.id
        );

      setMaterialConsumption(
        response?.data || []
      );

    } catch (error) {

      console.error(
        "Failed to load material consumption",
        error
      );
    }
  };  


  // ===========================================================================
  // EFFECT
  // ===========================================================================
  useEffect(() => {

    if (task?.id) {

      loadLatestExecutionStatus();

      loadExecutionHistory();

       loadMaterialConsumptionSummary();
    }

  }, [task]);


  // ===========================================================================
  // SAVE EXECUTION UPDATE
  // ===========================================================================
  const handleSaveExecutionUpdate =
    async (
      statusOverride = null
    ) => {

      try {

        setSaving(true);

        const finalStatus =
          statusOverride ||
          executionStatus;

        await createExecutionUpdate({

          project_id:
            task.project_id,

          phase_id:
            task.phase_id,

          task_id:
            task.id,

          execution_status:
            finalStatus,

          progress_percent:
            progressPercent,

          work_summary:
            workSummary,

          blockers:
            blockers,

          remarks:
            remarks
        });

        setExecutionStatus(
          finalStatus
        );

        alert(
          "Execution update saved successfully"
        );

        await loadLatestExecutionStatus();

        await loadExecutionHistory();

      } catch (error) {

        console.error(error);

        alert(

          error?.response?.data?.message ||

          "Failed to save execution update"
        );

      } finally {

        setSaving(false);
      }
    };


  // ===========================================================================
  // START TASK
  // ===========================================================================
  const handleStartTask =
    async () => {

      try {

        setExecutionStatus(
          "IN_PROGRESS"
        );

        await handleSaveExecutionUpdate(
          "IN_PROGRESS"
        );

      } catch (err) {

        console.error(
          "Failed to start task",
          err
        );
      }
    };


  // ===========================================================================
  // HOLD TASK
  // ===========================================================================
  const handleHoldTask =
    async () => {

      try {

        setExecutionStatus(
          "ON_HOLD"
        );

        await handleSaveExecutionUpdate(
          "ON_HOLD"
        );

      } catch (err) {

        console.error(
          "Failed to hold task",
          err
        );
      }
    };


  // ===========================================================================
  // COMPLETE TASK
  // ===========================================================================
  const handleCompleteTask =
    async () => {

      try {

        setExecutionStatus(
          "COMPLETED"
        );

        setProgressPercent(100);

        await handleSaveExecutionUpdate(
          "COMPLETED"
        );

      } catch (err) {

        console.error(
          "Failed to complete task",
          err
        );
      }
    };
    // ===========================================================================
// HANDLE CONSUMPTION INPUT
// ===========================================================================
const handleConsumptionInput =
  (
    materialId,
    field,
    value
  ) => {

    setConsumptionInputs((prev) => ({

      ...prev,

      [materialId]: {

        ...prev[materialId],

        [field]: value
      }
    }));
  };

// ===========================================================================
// HANDLE RETURN INPUT
// ===========================================================================
const handleReturnInput =
  (
    materialId,
    field,
    value
  ) => {

    setReturnInputs((prev) => ({

      ...prev,

      [materialId]: {

        ...prev[materialId],

        [field]: value
      }
    }));
  };

  // ===========================================================================
// HANDLE SCRAP INPUT
// ===========================================================================
const handleScrapInput =
  (
    materialId,
    field,
    value
  ) => {

    setScrapInputs((prev) => ({

      ...prev,

      [materialId]: {

        ...prev[materialId],

        [field]: value
      }
    }));
  };
// ===========================================================================
// SAVE MATERIAL CONSUMPTION
// ===========================================================================
const handleSaveConsumption =
  async (row) => {

    try {

      const input =
        consumptionInputs[
          row.material_id
        ] || {};

      const consumeQty =
        parseFloat(
          input.consume_qty || 0
        );

      if (consumeQty <= 0) {

        alert(
          "Enter valid consume qty"
        );

        return;
      }

      await saveMaterialConsumption({

        project_id:
          task.project_id,

        phase_id:
          task.phase_id,

        task_id:
          task.id,

        material_id:
          row.material_id,

        consume_qty:
          consumeQty,

        remarks:
          input.remarks || ""
      });

      alert(
        "Consumption saved successfully"
      );

      setConsumptionInputs((prev) => ({

        ...prev,

        [row.material_id]: {

          consume_qty: "",

          remarks: ""
        }
      }));

      await loadMaterialConsumptionSummary();

    } catch (error) {

      console.error(error);

      alert(

        error?.response?.data?.message ||

        "Failed to save consumption"
      );
    }
  };
// ===========================================================================
// SAVE MATERIAL RETURN
// ===========================================================================
const handleSaveReturn =
  async (row) => {

    try {

      const input =
        returnInputs[
          row.material_id
        ] || {};

      const returnQty =
        parseFloat(
          input.return_qty || 0
        );

      if (returnQty <= 0) {

        alert(
          "Enter valid return qty"
        );

        return;
      }

      if (returnQty > row.balance_qty) {

        alert(
          "Return qty exceeds balance qty"
        );

        return;
      }

      await createMaterialReturn({

        project_id:
          task.project_id,

        phase_id:
          task.phase_id,

        task_id:
          task.id,

        material_id:
          row.material_id,

        return_qty:
          returnQty,

        return_reason:
          input.return_reason || "",

        remarks:
          input.remarks || ""
      });

      alert(
        "Material returned successfully"
      );

      setReturnInputs((prev) => ({

        ...prev,

        [row.material_id]: {

          return_qty: "",

          return_reason: "",

          remarks: ""
        }
      }));

      await loadMaterialConsumptionSummary();

    } catch (error) {

      console.error(error);

      alert(

        error?.response?.data?.message ||

        "Failed to return material"
      );
    }
  };
  // ===========================================================================
// SAVE MATERIAL SCRAP
// ===========================================================================
const handleSaveScrap =
  async (row) => {

    try {

      const input =
        scrapInputs[
          row.material_id
        ] || {};

      const scrapQty =
        parseFloat(
          input.scrap_qty || 0
        );

      if (scrapQty <= 0) {

        alert(
          "Enter valid scrap qty"
        );

        return;
      }

      if (scrapQty > row.balance_qty) {

        alert(
          "Scrap qty exceeds balance qty"
        );

        return;
      }

      await createMaterialScrap({

        project_id:
          task.project_id,

        phase_id:
          task.phase_id,

        task_id:
          task.id,

        material_id:
          row.material_id,

        scrap_qty:
          scrapQty,

        scrap_reason:
          input.scrap_reason || "",

        remarks:
          input.remarks || ""
      });

      alert(
        "Material scrap saved successfully"
      );

      setScrapInputs((prev) => ({

        ...prev,

        [row.material_id]: {

          scrap_qty: "",

          scrap_reason: "",

          remarks: ""
        }
      }));

      await loadMaterialConsumptionSummary();

    } catch (error) {

      console.error(error);

      alert(

        error?.response?.data?.message ||

        "Failed to save scrap"
      );
    }
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.container}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div style={styles.sectionHeader}>


        <div>

          <div style={styles.title}>

            Task Execution Control

          </div>


          <div style={styles.subtitle}>


            Execution lifecycle,
            operational progress,
            and task control actions

          </div>

        </div>

      </div>


      {/* ================================================================ */}
      {/* STATUS CARD */}
      {/* ================================================================ */}
      <div style={styles.statusCard}>


        <div style={styles.statusTop}>


          <div>


            <div style={styles.statusLabel}>

              Current Execution Status

            </div>


            <div style={
              getExecutionStatusStyle(
                executionStatus
              )
            }>

              {
                formatStatus(
                  executionStatus
                )
              }

            </div>

          </div>


          <div style={styles.readinessBox}>


            <div style={styles.readinessValue}>


              {

                task.readiness_percent || 0

              }%

            </div>


            <div style={styles.readinessLabel}>


              Execution Readiness

            </div>

          </div>

        </div>

      </div>


      {/* ================================================================ */}
      {/* PROGRESS CARD */}
      {/* ================================================================ */}
      <div style={styles.notesCard}>


        <div style={styles.notesTitle}>

          Execution Progress

        </div>


        <div style={styles.progressGrid}>


          <div>

            <div style={styles.inputLabel}>

              Execution Status

            </div>

            <select

              value={executionStatus}

              onChange={(e) =>
                setExecutionStatus(
                  e.target.value
                )
              }

              style={styles.selectInput}
            >

              <option value="NOT_STARTED">
                NOT_STARTED
              </option>

              <option value="IN_PROGRESS">
                IN_PROGRESS
              </option>

              <option value="ON_HOLD">
                ON_HOLD
              </option>

              <option value="BLOCKED">
                BLOCKED
              </option>

              <option value="COMPLETED">
                COMPLETED
              </option>

            </select>

          </div>


          <div>

            <div style={styles.inputLabel}>

              Progress %

            </div>

            <input

              type="number"

              value={progressPercent}

              onChange={(e) =>
                setProgressPercent(
                  e.target.value
                )
              }

              min={0}

              max={100}

              style={styles.textInput}
            />

          </div>

        </div>


        <div style={styles.fieldBlock}>


          <div style={styles.inputLabel}>

            Work Summary

          </div>

          <textarea

            style={styles.notesInput}

            value={workSummary}

            onChange={(e) =>
              setWorkSummary(
                e.target.value
              )
            }

            placeholder="Enter work completed summary..."
          />

        </div>


        <div style={styles.fieldBlock}>


          <div style={styles.inputLabel}>

            Blockers / Issues

          </div>

          <textarea

            style={styles.notesInput}

            value={blockers}

            onChange={(e) =>
              setBlockers(
                e.target.value
              )
            }

            placeholder="Enter blockers, dependencies, delays..."
          />

        </div>

      </div>


      {/* ================================================================ */}
      {/* MATERIAL CONSUMPTION */}
      {/* ================================================================ */}
      <div style={styles.notesCard}>


        <div style={styles.notesTitle}>

          Material Consumption

        </div>


        {

          materialConsumption.length === 0

            ? (

              <div style={styles.emptyHistory}>

                No issued materials available

              </div>
            )

            : (

              <div style={styles.materialTable}>


                <div style={styles.materialHeader}>


                <div>Material</div>

                <div>Issued</div>

                <div>Consumed</div>

                <div>Returned</div>

                <div>Scrapped</div>

                <div>Balance</div>

                <div>Consume Qty</div>

                <div>Consume</div>

                <div>Return Qty</div>

                <div>Return</div>

                <div>Scrap Qty</div>

                <div>Scrap</div>
                

              </div>


                {

                  materialConsumption.map((row) => (

                    <div
                      key={row.material_id}
                      style={styles.materialRow}
                    >


                      <div>

                        {
                          row.material_name
                        }

                      </div>


                      <div>

                        {
                          row.issued_qty
                        }

                      </div>


                      <div>

                        {
                          row.consumed_qty
                        }

                      </div>
                      <div>
                        {row.returned_qty || 0}
                      </div>

                      <div>
                        {row.scrap_qty || 0}
                      </div>


                      <div>

                        {
                          row.balance_qty
                        }

                      </div>


                      <div>

                        <input

                          type="number"

                          min={0}

                          max={row.balance_qty}

                          value={
                            consumptionInputs[
                              row.material_id
                            ]?.consume_qty || ""
                          }

                          onChange={(e) =>
                            handleConsumptionInput(

                              row.material_id,

                              "consume_qty",

                              e.target.value
                            )
                          }

                          style={styles.consumeInput}
                        />

                      </div>


                      <div>

                        <button

                          style={styles.consumeBtn}

                          onClick={() =>
                            handleSaveConsumption(row)
                          }
                        >

                          SAVE

                        </button>

                      </div>
                      <div>

                        <input

                          type="number"

                          min={0}

                          max={row.balance_qty}

                          value={
                            returnInputs[
                              row.material_id
                            ]?.return_qty || ""
                          }

                          onChange={(e) =>
                            handleReturnInput(

                              row.material_id,

                              "return_qty",

                              e.target.value
                            )
                          }

                          style={styles.consumeInput}
                        />

                      </div>

                      <div>

                        <button

                          style={styles.returnBtn}

                          onClick={() =>
                            handleSaveReturn(row)
                          }
                        >

                          RETURN

                        </button>

                      </div>
                      <div>

                        <input

                          type="number"

                          min={0}

                          max={row.balance_qty}

                          value={
                            scrapInputs[
                              row.material_id
                            ]?.scrap_qty || ""
                          }

                          onChange={(e) =>
                            handleScrapInput(

                              row.material_id,

                              "scrap_qty",

                              e.target.value
                            )
                          }

                          style={styles.consumeInput}
                        />

                      </div>

                      <div>

                        <button

                          style={styles.scrapBtn}

                          onClick={() =>
                            handleSaveScrap(row)
                          }
                        >

                          SCRAP

                        </button>

                      </div>

                    </div>
                    //</div>
                  ))
                }

              </div>
            )
        }

      </div>
      {/* ================================================================ */}
      {/* ACTION PANEL */}
      {/* ================================================================ */}
      <div style={styles.actionGrid}>


        {/* ============================================================ */}
        {/* START */}
        {/* ============================================================ */}
        <div style={styles.actionCard}>


          <div style={styles.actionIcon}>

            <Play size={28} />

          </div>


          <div style={styles.actionTitle}>

            Start Task

          </div>


          <div style={styles.actionDescription}>


            Begin execution activity
            for this task

          </div>


          <button
            style={styles.startBtn}
            onClick={handleStartTask}
            disabled={saving}
          >

            START EXECUTION

          </button>

        </div>


        {/* ============================================================ */}
        {/* HOLD */}
        {/* ============================================================ */}
        <div style={styles.actionCard}>


          <div style={styles.actionIcon}>

            <PauseCircle size={28} />

          </div>


          <div style={styles.actionTitle}>

            Hold Task

          </div>


          <div style={styles.actionDescription}>


            Pause execution due to
            dependency or site issue

          </div>


          <button
            style={styles.holdBtn}
            onClick={handleHoldTask}
            disabled={saving}
          >

            HOLD TASK

          </button>

        </div>


        {/* ============================================================ */}
        {/* COMPLETE */}
        {/* ============================================================ */}
        <div style={styles.actionCard}>


          <div style={styles.actionIcon}>

            <CheckCircle2 size={28} />

          </div>


          <div style={styles.actionTitle}>

            Complete Task

          </div>


          <div style={styles.actionDescription}>


            Mark execution as fully
            completed

          </div>


          <button
            style={styles.completeBtn}
            onClick={handleCompleteTask}
            disabled={saving}
          >

            COMPLETE TASK

          </button>

        </div>

      </div>


      {/* ================================================================ */}
      {/* EXECUTION NOTES */}
      {/* ================================================================ */}
      <div style={styles.notesCard}>


        <div style={styles.notesTitle}>

          Execution Notes

        </div>


        <textarea
          style={styles.notesInput}

          value={remarks}

          onChange={(e) =>
            setRemarks(
              e.target.value
            )
          }

          placeholder={
            "Enter execution notes, delays, site remarks, dependencies, or completion comments..."
          }
        />


        <div style={styles.notesFooter}>


          <button
            style={styles.saveBtn}
            onClick={() =>
              handleSaveExecutionUpdate()
            }
            disabled={saving}
          >

            {

              saving

                ? "SAVING..."

                : "SAVE EXECUTION UPDATE"
            }

          </button>

        </div>

      </div>


      {/* ================================================================ */}
      {/* EXECUTION HISTORY */}
      {/* ================================================================ */}
      <div style={styles.notesCard}>


        <div style={styles.notesTitle}>

          Execution History

        </div>


        {

          executionHistory.length === 0

            ? (

              <div style={styles.emptyHistory}>

                No execution updates available

              </div>
            )

            : (

              <div style={styles.timelineContainer}>


                {

                  executionHistory.map((row) => (

                    <div
                      key={row.id}
                      style={styles.timelineItem}
                    >


                      <div style={styles.timelineHeader}>


                        <div style={
                          getExecutionStatusStyle(
                            row.execution_status
                          )
                        }>

                          {
                            formatStatus(
                              row.execution_status
                            )
                          }

                        </div>


                        <div style={styles.timelineDate}>


                          {
                            row.created_at
                          }

                        </div>

                      </div>


                      <div style={styles.timelineProgress}>


                        Progress:
                        {" "}
                        <strong>

                          {
                            row.progress_percent
                          }%

                        </strong>

                      </div>


                      {

                        row.work_summary && (

                          <div style={styles.timelineSection}>


                            <strong>
                              Work Summary:
                            </strong>

                            <div>

                              {
                                row.work_summary
                              }

                            </div>

                          </div>
                        )
                      }


                      {

                        row.blockers && (

                          <div style={styles.timelineSection}>


                            <strong>
                              Blockers:
                            </strong>

                            <div>

                              {
                                row.blockers
                              }

                            </div>

                          </div>
                        )
                      }


                      {

                        row.remarks && (

                          <div style={styles.timelineSection}>


                            <strong>
                              Remarks:
                            </strong>

                            <div>

                              {
                                row.remarks
                              }

                            </div>

                          </div>
                        )
                      }

                    </div>
                  ))
                }

              </div>
            )
        }

      </div>

    </div>
  );
}


// =============================================================================
// HELPERS
// =============================================================================

function formatStatus(status) {

  if (!status) {
    return "-";
  }

  return status
    .replaceAll("_", " ");
}


function getExecutionStatusStyle(status) {

  switch (status) {

    case "IN_PROGRESS":

      return styles.statusBlue;

    case "ON_HOLD":

      return styles.statusOrange;

    case "COMPLETED":

      return styles.statusGreen;

    case "NOT_STARTED":

      return styles.statusGray;

    case "BLOCKED":

      return styles.statusRed;

    default:

      return styles.statusGray;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  container: {

    display: "flex",

    flexDirection: "column",

    gap: 20
  },

  sectionHeader: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 18,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  title: {

    fontSize: 18,

    fontWeight: 700,

    color: "#111827"
  },

  subtitle: {

    marginTop: 6,

    fontSize: 13,

    color: "#6b7280"
  },

  statusCard: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 24,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  statusTop: {

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  statusLabel: {

    fontSize: 13,

    color: "#6b7280",

    marginBottom: 10
  },

  readinessBox: {

    textAlign: "center"
  },

  readinessValue: {

    fontSize: 34,

    fontWeight: 700,

    color: "#111827"
  },

  readinessLabel: {

    fontSize: 12,

    color: "#6b7280",

    marginTop: 6
  },

  progressGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(2, 1fr)",

    gap: 20
  },

  fieldBlock: {

    marginTop: 20
  },

  inputLabel: {

    fontSize: 13,

    fontWeight: 600,

    color: "#374151",

    marginBottom: 8
  },

  selectInput: {

    width: "100%",

    height: 42,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    padding: "0 12px",

    fontSize: 14,

    outline: "none"
  },

  textInput: {

    width: "100%",

    height: 42,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    padding: "0 12px",

    fontSize: 14,

    outline: "none"
  },

  actionGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(3, 1fr)",

    gap: 20
  },

  actionCard: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 24,

    display: "flex",

    flexDirection: "column",

    alignItems: "center",

    textAlign: "center",

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  actionIcon: {

    marginBottom: 14,

    color: "#2563eb"
  },

  actionTitle: {

    fontSize: 16,

    fontWeight: 700,

    color: "#111827",

    marginBottom: 10
  },

  actionDescription: {

    fontSize: 13,

    color: "#6b7280",

    lineHeight: "22px",

    marginBottom: 20
  },

  startBtn: {

    height: 40,

    border: "none",

    background: "#2563eb",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 18px",

    cursor: "pointer",

    fontWeight: 700
  },

  holdBtn: {

    height: 40,

    border: "none",

    background: "#ea580c",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 18px",

    cursor: "pointer",

    fontWeight: 700
  },

  completeBtn: {

    height: 40,

    border: "none",

    background: "#16a34a",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 18px",

    cursor: "pointer",

    fontWeight: 700
  },

  notesCard: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  notesTitle: {

    fontSize: 16,

    fontWeight: 700,

    color: "#111827",

    marginBottom: 16
  },

  notesInput: {

    width: "100%",

    minHeight: 140,

    borderRadius: 10,

    border: "1px solid #d1d5db",

    padding: 14,

    resize: "vertical",

    fontSize: 14,

    outline: "none"
  },

  notesFooter: {

    marginTop: 16,

    display: "flex",

    justifyContent: "flex-end"
  },

  saveBtn: {

    height: 40,

    border: "none",

    background: "#111827",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 18px",

    cursor: "pointer",

    fontWeight: 700
  },

  timelineContainer: {

    display: "flex",

    flexDirection: "column",

    gap: 16
  },

  materialTable: {

  display: "flex",

  flexDirection: "column",

  border: "1px solid #e5e7eb",

  borderRadius: 12,

  overflow: "hidden"
},

materialHeader: {

  display: "grid",

  gridTemplateColumns:
    "2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr",

  background: "#f3f4f6",

  padding: 14,

  fontWeight: 700,

  fontSize: 13
},

materialRow: {

  display: "grid",

  gridTemplateColumns:
    "2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr",

  padding: 14,

  borderTop:
    "1px solid #e5e7eb",

  fontSize: 14
},

  timelineItem: {

    border: "1px solid #e5e7eb",

    borderRadius: 12,

    padding: 16,

    background: "#f9fafb"
  },

  timelineHeader: {

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    marginBottom: 12
  },

  timelineDate: {

    fontSize: 12,

    color: "#6b7280"
  },

  timelineProgress: {

    fontSize: 14,

    color: "#111827",

    marginBottom: 10
  },

  timelineSection: {

    marginTop: 10,

    fontSize: 13,

    color: "#374151",

    lineHeight: "22px"
  },

  emptyHistory: {

    padding: 20,

    textAlign: "center",

    color: "#6b7280",

    fontSize: 14
  },
  consumeInput: {

  width: "100%",

  height: 36,

  borderRadius: 8,

  border: "1px solid #d1d5db",

  padding: "0 10px",

  fontSize: 14,

  outline: "none"
},

consumeBtn: {

  height: 36,

  border: "none",

  background: "#2563eb",

  color: "#ffffff",

  borderRadius: 8,

  padding: "0 14px",

  cursor: "pointer",

  fontWeight: 700
},
returnBtn: {

  height: 36,

  border: "none",

  background: "#7c3aed",

  color: "#ffffff",

  borderRadius: 8,

  padding: "0 14px",

  cursor: "pointer",

  fontWeight: 700
},
scrapBtn: {

  height: 36,

  border: "none",

  background: "#dc2626",

  color: "#ffffff",

  borderRadius: 8,

  padding: "0 14px",

  cursor: "pointer",

  fontWeight: 700
},
  
  statusBlue: {

    display: "inline-flex",

    background: "#dbeafe",

    color: "#1d4ed8",

    padding: "8px 14px",

    borderRadius: 999,

    fontSize: 13,

    fontWeight: 700
  },

  statusOrange: {

    display: "inline-flex",

    background: "#ffedd5",

    color: "#c2410c",

    padding: "8px 14px",

    borderRadius: 999,

    fontSize: 13,

    fontWeight: 700
  },

  statusGreen: {

    display: "inline-flex",

    background: "#dcfce7",

    color: "#166534",

    padding: "8px 14px",

    borderRadius: 999,

    fontSize: 13,

    fontWeight: 700
  },

  statusGray: {

    display: "inline-flex",

    background: "#e5e7eb",

    color: "#374151",

    padding: "8px 14px",

    borderRadius: 999,

    fontSize: 13,

    fontWeight: 700
  },

  statusRed: {

    display: "inline-flex",

    background: "#fee2e2",

    color: "#b91c1c",

    padding: "8px 14px",

    borderRadius: 999,

    fontSize: 13,

    fontWeight: 700
  }
};